import ContractualAccountDTO from 'features/contracts/dtos/ContractualAccountDTO';
import CommodityIconList from 'features/shared/components/icons/CommodityIconList';
import AddressDTO from 'features/shared/dtos/AddressDTO';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { getAddressSections } from 'features/shared/helpers/AddressFormatter';
import React from 'react';
import Select, { components, createFilter, OptionsType, MultiValueProps } from "react-select";
import { Config } from 'react-select/src/filters';
import styles from './contractualaccountselect.module.scss';
import reactSelectStyles from 'features/shared/components/reactSelect/reactselectcustom.module.scss';
import { useTranslation } from 'react-i18next';

interface Props {
  name: string;
  contractualAccounts: ContractualAccountDTO[];
  onChange: (contractulAccountIdsCsv: string) => void;
  errors?: Record<string, string>;
}

const ContractualAccountSelect = ({ name, contractualAccounts, onChange, errors }: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // helpers
  const getInlineAddress = (address: AddressDTO): string => {
    let sections = getAddressSections(address);    
    return `${sections.streetSection}, ${sections.citySection}`;
  }

  // filter setting
  const filterConfig: Config = {
    stringify: (obj: any) => {
      const { number, address } = obj.data as ContractualAccountDTO;
      const inlineAddress = getInlineAddress(address!);
      return `${number} ${inlineAddress}`;
    }
  };

  // handlers
  const selectAccountChangeHandler = (accounts: OptionsType<ContractualAccountDTO>) => {
    const accountIds = accounts.map((account) => account.contractualAccountId!);
    onChange(accountIds.join(","));
  }

  // sub-renders
  const renderOption = (account: ContractualAccountDTO) => {
    return (
      <div className="d-flex align-items-center">
        <CommodityIconList commodityDetails={account.commodityDetail!} includeColor className={styles.icons} />
        <div className={styles.textContent}>
          <div className="text-truncate">
            <span>{account.number}</span>
          </div>
          <div className="small text-muted text-truncate mt-n1">
            {getInlineAddress(account.address!)}
          </div>
        </div>
      </div>
    );
  }

  const renderMultiValue = (props: MultiValueProps<ContractualAccountDTO>) => (
    <components.MultiValue {...props}>
      {props.data.number}
    </components.MultiValue>
  );

  // render
  return (
    <FormGroup labelText={tLabels("contractualAccountSelect.contractualAccount")} validationError={errors ? errors[name] : undefined}>
      <Select
        isMulti
        hideSelectedOptions={false}
        options={contractualAccounts}
        closeMenuOnSelect={false}
        getOptionLabel={(account) => account.number!}
        getOptionValue={(account) => account.number!}
        onChange={(val) => selectAccountChangeHandler(val)}
        formatOptionLabel={(account) => renderOption(account)}
        components={{MultiValue: renderMultiValue}}
        filterOption={createFilter(filterConfig)}
        captureMenuScroll={false}
        className={`${reactSelectStyles.customSelect}`}
        classNamePrefix="customSelect"
        placeholder={tLabels("contractualAccountSelect.setContractualAccounts")}
        noOptionsMessage={() => tLabels("contractualAccountSelect.message")}
      />
    </FormGroup>
  )
}

export default ContractualAccountSelect;
